import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Loader, Skeleton, Table, Text, Tooltip } from "@mantine/core";
import { CheckIcon } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  danger,
  gray,
  info,
  primary,
  success,
  warning,
} from "../../constants/color-constants";
import { VBCAlertRow } from "../../types/vbc-alerts";
import { buildVBCAlertGroupKey } from "../../utils/vbc-alert-utils";
import {
  formatSeriesNumber,
  getSeriesNumberColumnStyle,
} from "../../utils/series-number-column";
import CheckBoxIcon from "../../assets/AlertCheckBox.svg";
import ActiveCheckBoxIcon from "../../assets/AlertCheckBoxActive.svg";
import PendingIcon from "../../assets/PendingIcon.svg";

type VBCAlertsV2TableProps = {
  alerts: VBCAlertRow[];
  loading?: boolean;
  onAcknowledge?: (alerts: VBCAlertRow[]) => Promise<boolean> | boolean | void;
};

interface VBCAlertGroup {
  groupKey: string;
  v28: string;
  icdCode: string;
  icdCodeDescription: string;
  description: string;
  createdAt: string;
  kind: VBCAlertRow["kind"];
  status: VBCAlertRow["status"];
  alertId?: string;
  alerts: VBCAlertRow[];
}

const getAlertDescription = (alert: VBCAlertRow) => {
  if (alert.kind === "SPECIALIST_VISIT" || alert.kind === "HOSPITALIZATION") {
    return alert.description;
  }
  return alert.icdCodeDescription;
};

const getAlertCreatedAt = (alert: VBCAlertRow) => {
  if (alert.kind === "QUERY") {
    return alert.dos;
  }
  if (alert.kind === "SPECIALIST_VISIT" || alert.kind === "HOSPITALIZATION") {
    return alert.createdAt;
  }
  if (alert.kind === "SUSPECT_DIAGNOSIS") {
    return alert.labResultDate;
  }
  return "";
};

const getQuerySortTimestamp = (alert: VBCAlertRow) => {
  if (alert.kind !== "QUERY") {
    return 0;
  }

  const timestamp = alert.dos ? Date.parse(alert.dos) : Number.NaN;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const KIND_SORT_ORDER: Record<VBCAlertRow["kind"], number> = {
  QUERY: 0,
  SUSPECT_DIAGNOSIS: 1,
  HOSPITALIZATION: 2,
  SPECIALIST_VISIT: 3,
};

const groupAlertsByHCC = (alerts: VBCAlertRow[]): VBCAlertGroup[] => {
  const groupMap = new Map<string, VBCAlertGroup>();

  alerts.forEach((alert) => {
    const groupKey = buildVBCAlertGroupKey(alert);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        groupKey,
        v28: alert.v28,
        icdCode: alert.icdCode,
        icdCodeDescription: alert.icdCodeDescription,
        description: getAlertDescription(alert),
        createdAt: getAlertCreatedAt(alert),
        kind: alert.kind,
        status: alert.status,
        alertId: alert.alertId,
        alerts: [],
      });
    }

    const group = groupMap.get(groupKey)!;
    group.alerts.push(alert);
    if (alert.status === "ACKNOWLEDGED") {
      group.status = "ACKNOWLEDGED";
    }
  });

  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.kind !== b.kind) {
      return KIND_SORT_ORDER[a.kind] - KIND_SORT_ORDER[b.kind];
    }

    if (a.kind === "QUERY" && b.kind === "QUERY") {
      return getQuerySortTimestamp(b.alerts[0]) - getQuerySortTimestamp(a.alerts[0]);
    }

    if (a.kind === "SPECIALIST_VISIT" || a.kind === "HOSPITALIZATION") {
      return a.description.localeCompare(b.description);
    }

    const aNum = parseFloat(a.v28) || 0;
    const bNum = parseFloat(b.v28) || 0;
    return aNum - bNum;
  });
};

const formatDateOnly = (value?: string) => {
  if (!value) {
    return "--";
  }

  const trimmed = value.trim();

  // Already MM-DD-YYYY (with optional time suffix)
  const mdy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (mdy) {
    return `${mdy[1]}-${mdy[2]}-${mdy[3]}`;
  }

  // YYYY-MM-DD (with optional time suffix)
  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return `${ymd[2]}-${ymd[3]}-${ymd[1]}`;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }

  const pad = (input: number) => input.toString().padStart(2, "0");
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
};

const normalizeSecondaryContent = (text?: string | null) =>
  (text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\s*\n\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const getGroupSecondaryText = (group: VBCAlertGroup) => {
  const alert = group.alerts[0];
  if (!alert) {
    return "";
  }

  if (alert.kind === "SUSPECT_DIAGNOSIS") {
    return normalizeSecondaryContent(alert.source);
  }

  if (alert.kind === "QUERY") {
    return normalizeSecondaryContent(
      alert.queryEvidence?.replace(/\s*\(page\s+\d+\)/gi, "")
    );
  }

  if (alert.kind === "HOSPITALIZATION") {
    return alert.principalDiagnosis?.trim() || "";
  }

  return "";
};

const getTypeBadgeStyles = (kind: VBCAlertRow["kind"]) => {
  if (kind === "QUERY") {
    return {
      color: warning[5],
      backgroundColor: warning[1],
      borderColor: warning[5],
      label: "Quality Measure",
    };
  }

  if (kind === "SPECIALIST_VISIT") {
    return {
      color: success[6],
      backgroundColor: success[0],
      borderColor: success[6],
      label: "Recommended Specialist Visit",
    };
  }

  if (kind === "HOSPITALIZATION") {
    return {
      color: warning[5],
      backgroundColor: warning[0],
      borderColor: warning[5],
      label: "Hospitalization",
    };
  }

  return {
    color: danger[4],
    backgroundColor: danger[0],
    borderColor: danger[4],
    label: "Suspected HCC Diagnosis",
  };
};

type SectionColumnId =
  | "series"
  | "icdCode"
  | "icdDescription"
  | "description"
  | "type"
  | "createdAt"
  | "action"
  | "expand";

interface SectionColumnDef {
  id: SectionColumnId;
  header: string;
  width?: string;
  minWidth?: string;
  /** How many table column slots this cell occupies (e.g. Description spans ICD Code + Description). */
  colSpan?: number;
}

/** Shared slot count across all sections so column widths stay aligned. */
const TABLE_COLUMN_SLOT_COUNT = 6;

/** Shared left inset so DOS / Admission headers and values share the same start. */
const DATE_COLUMN_PADDING_LEFT = 48;
/** Horizontal space between ICD-10 Code and ICD-10 Description. */
const ICD_COLUMN_GAP = 20;

/** Columns kept in config per type so sections can differ (e.g. no ICD later). */
const ICD_SECTION_COLUMNS: SectionColumnDef[] = [
  { id: "series", header: "#" },
  { id: "icdCode", header: "ICD-10 Code", width: "110px", minWidth: "110px" },
  {
    id: "icdDescription",
    header: "ICD-10 Description",
    minWidth: "360px",
  },
  { id: "createdAt", header: "DOS", width: "240px", minWidth: "240px" },
  { id: "action", header: "Action/Status", width: "170px", minWidth: "150px" },
  { id: "expand", header: "", width: "64px", minWidth: "64px" },
];

/**
 * No DOS column; description absorbs code + description + date slots
 * so Action/Status stays aligned.
 */
const SPECIALIST_SECTION_COLUMNS: SectionColumnDef[] = [
  { id: "series", header: "#" },
  {
    id: "description",
    header: "Description",
    minWidth: "560px",
    colSpan: 3,
  },
  { id: "action", header: "Action/Status", width: "170px", minWidth: "150px" },
  { id: "expand", header: "", width: "64px", minWidth: "64px" },
];

const HOSPITALIZATION_SECTION_COLUMNS: SectionColumnDef[] = [
  { id: "series", header: "#" },
  {
    id: "description",
    header: "Description",
    minWidth: "400px",
    colSpan: 2,
  },
  {
    id: "createdAt",
    header: "Admission / Discharge",
    width: "240px",
    minWidth: "240px",
  },
  { id: "action", header: "Action/Status", width: "170px", minWidth: "150px" },
  { id: "expand", header: "", width: "64px", minWidth: "64px" },
];

const getSectionColumnSlotCount = (columns: SectionColumnDef[]) =>
  columns.reduce((sum, column) => sum + (column.colSpan ?? 1), 0);

/** Colors are fixed by alert type so filters keep the same section color. */
const ALERT_SECTIONS: Array<{
  kind: VBCAlertRow["kind"];
  title: string;
  backgroundColor: string;
  columns: SectionColumnDef[];
}> = [
  {
    kind: "QUERY",
    title: "QUALITY MEASURES",
    backgroundColor: "#FFFDF5", // pastel yellow
    columns: ICD_SECTION_COLUMNS,
  },
  {
    kind: "SUSPECT_DIAGNOSIS",
    title: "SUSPECTED HCC DIAGNOSIS",
    backgroundColor: "#FAF8FF", // pastel purple
    columns: ICD_SECTION_COLUMNS,
  },
  {
    kind: "HOSPITALIZATION",
    title: "HOSPITALIZATIONS",
    backgroundColor: "#F5F9FF", // pastel blue
    columns: HOSPITALIZATION_SECTION_COLUMNS,
  },
  {
    kind: "SPECIALIST_VISIT",
    title: "RECOMMENDED SPECIALIST VISITS",
    backgroundColor: "#F5FBF0", // pastel green
    columns: SPECIALIST_SECTION_COLUMNS,
  },
];

const buildAlertSections = (groups: VBCAlertGroup[]) =>
  ALERT_SECTIONS.map((section) => ({
    ...section,
    groups: groups.filter((group) => group.kind === section.kind),
  })).filter((section) => section.groups.length > 0);

const thStyle: React.CSSProperties = {
  padding: "6px 16px",
  borderBottom: "none",
  fontSize: "14px",
  fontWeight: 600,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const buildCellStyle = (rowBg: string): React.CSSProperties => ({
  borderBottom: `1px solid ${gray[2]}`,
  padding: "10px 16px",
  backgroundColor: rowBg,
  verticalAlign: "top",
});

const buildExpandCellStyle = (rowBg: string): React.CSSProperties => ({
  borderBottom: `1px solid ${gray[2]}`,
  padding: "10px 16px",
  backgroundColor: rowBg,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
});

const renderSecondaryText = (content: string) => {
  if (!content) {
    return null;
  }

  return (
    <Text
      size='sm'
      c={gray[6]}
      fs='italic'
      mt={4}
      style={{
        display: "block",
        width: "100%",
        lineHeight: 1.4,
        overflowWrap: "anywhere",
        wordBreak: "normal",
        whiteSpace: "normal",
      }}
    >
      {content}
    </Text>
  );
};

const renderHccBadge = (v28: string) => (
  <Badge
    variant='light'
    radius='xl'
    size='sm'
    style={{
      marginTop: "6px",
      color: primary[5],
      backgroundColor: primary[0],
      border: `1px solid ${primary[5]}`,
      fontWeight: 700,
      textTransform: "none",
      width: "fit-content",
    }}
  >
    HCC-{v28 || "--"}
  </Badge>
);

const renderActionStatus = (
  alerts: VBCAlertRow[],
  status: string,
  rowBackgroundColor: string,
  options?: {
    isAcknowledging?: boolean;
    onAcknowledge?: (alerts: VBCAlertRow[]) => void;
  }
) => {
  const { isAcknowledging = false, onAcknowledge } = options || {};
  const pendingAlerts = alerts.filter((alert) => alert.status === "PENDING");
  const canAcknowledge = status === "PENDING" && !isAcknowledging;

  if (status === "PENDING") {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
        }}
        onClick={(event: React.MouseEvent) => {
          // Keep row expand/collapse from stealing action clicks
          event.stopPropagation();
        }}
      >
        <Box
          component='img'
          src={isAcknowledging ? ActiveCheckBoxIcon : CheckBoxIcon}
          mb={4}
          style={{
            cursor: canAcknowledge ? "pointer" : "default",
            backgroundColor: rowBackgroundColor,
          }}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            if (canAcknowledge) {
              onAcknowledge?.(
                pendingAlerts.length > 0 ? pendingAlerts : alerts
              );
            }
          }}
        />
        <Badge
          color={info[4]}
          variant='light'
          radius='sm'
          size='xs'
          style={{
            border: `1px solid ${info[4]}`,
            height: "20px",
            lineHeight: "22px",
            padding: "0px 8px",
            minWidth: "75px",
            maxWidth: "120px",
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            textTransform: "none",
          }}
        >
          {isAcknowledging ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                minWidth: "75px",
                height: "100%",
              }}
            >
              <Loader size={12} color={primary[5]} />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                fontSize: "var(--mantine-font-size-xs)",
                minWidth: "75px",
              }}
            >
              <Box component='img' src={PendingIcon} mb={1} />
              Pending
            </div>
          )}
        </Badge>
      </Box>
    );
  }

  return (
    <Badge
      color={success[6]}
      variant='light'
      radius='sm'
      size='xs'
      style={{
        border: `1px solid ${success[6]}`,
        height: "20px",
        lineHeight: "22px",
        padding: "0px 8px",
        minWidth: "75px",
        maxWidth: "120px",
        width: "fit-content",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        textTransform: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
          fontSize: "var(--mantine-font-size-xs)",
        }}
      >
        <CheckIcon
          color={success[6]}
          style={{
            height: "20px",
            width: "10px",
          }}
        />
        Acknowledged
      </div>
    </Badge>
  );
};

const VBCAlertsV2Table: React.FC<VBCAlertsV2TableProps> = ({
  alerts,
  loading = false,
  onAcknowledge,
}) => {
  const [acknowledgingKeys, setAcknowledgingKeys] = useState<Set<string>>(
    () => new Set()
  );

  const groups = useMemo(() => groupAlertsByHCC(alerts), [alerts]);
  const sections = useMemo(() => buildAlertSections(groups), [groups]);
  const maxColumnCount = useMemo(
    () =>
      sections.reduce(
        (max, section) =>
          Math.max(max, getSectionColumnSlotCount(section.columns)),
        TABLE_COLUMN_SLOT_COUNT
      ),
    [sections]
  );

  const seriesColStyle = useMemo(
    () => getSeriesNumberColumnStyle(groups.length),
    [groups.length]
  );

  useEffect(() => {
    setAcknowledgingKeys((current) => {
      if (current.size === 0) {
        return current;
      }

      const pendingKeys = new Set(
        groups
          .filter((group) => group.status === "PENDING")
          .map((group) => group.groupKey)
      );
      const next = new Set(
        Array.from(current).filter((groupKey) => pendingKeys.has(groupKey))
      );
      return next.size === current.size ? current : next;
    });
  }, [groups]);

  const renderColumnHeader = (
    column: SectionColumnDef,
    sectionBackground: string
  ) => {
    const isSeries = column.id === "series";
    const isDateColumn = column.id === "createdAt";
    return (
      <Table.Th
        key={column.id}
        colSpan={column.colSpan}
        style={{
          ...thStyle,
          ...(isSeries ? { ...seriesColStyle, padding: "6px 4px" } : null),
          ...(column.id === "icdCode"
            ? { paddingRight: `${ICD_COLUMN_GAP}px` }
            : null),
          ...(column.id === "icdDescription"
            ? { paddingLeft: `${ICD_COLUMN_GAP}px` }
            : null),
          ...(isDateColumn
            ? {
                padding: `6px 16px 6px ${DATE_COLUMN_PADDING_LEFT}px`,
              }
            : null),
          width: column.width,
          minWidth: column.minWidth,
          backgroundColor: sectionBackground,
          boxSizing: "border-box",
        }}
      >
        {column.header}
      </Table.Th>
    );
  };

  return (
    <Box
      data-table-container
      className='reusable-table-scroll'
      style={{
        overflowX: "auto",
        overflowY: "auto",
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
        backgroundColor: gray[0],
      }}
    >
      <div style={{ minWidth: "1180px" }}>
        <Table
          highlightOnHover={!loading}
          highlightOnHoverColor={gray[0]}
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <colgroup>
            <col style={{ width: seriesColStyle.width ?? "48px" }} />
            <col style={{ width: "110px" }} />
            <col />
            <col style={{ width: "240px" }} />
            <col style={{ width: "170px" }} />
            <col style={{ width: "64px" }} />
          </colgroup>
          <Table.Tbody>
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <Table.Tr key={`skeleton-${index}`}>
                    <Table.Td
                      colSpan={maxColumnCount}
                      style={{ padding: "18px 16px" }}
                    >
                      <Skeleton height={40} radius='md' mb={10} />
                      <Skeleton height={80} radius='md' />
                    </Table.Td>
                  </Table.Tr>
                ))
              : sections.map((section, sectionIndex) => {
                  const columnIds = new Set(
                    section.columns.map((column) => column.id)
                  );
                  const colSpan = getSectionColumnSlotCount(section.columns);
                  const descriptionColSpan =
                    section.columns.find((column) => column.id === "description")
                      ?.colSpan ?? 1;

                  return (
                    <React.Fragment key={section.kind}>
                      <Table.Tr>
                        <Table.Td
                          colSpan={colSpan}
                          style={{
                            padding: "10px 16px",
                            backgroundColor: section.backgroundColor,
                            borderBottom: "none",
                            borderTop:
                              sectionIndex > 0
                                ? `1px solid ${gray[4]}`
                                : "none",
                          }}
                        >
                          <Text
                            fw={700}
                            size='sm'
                            c={danger[4]}
                            style={{ textTransform: "uppercase" }}
                          >
                            {section.title}
                          </Text>
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr>
                        {section.columns.map((column) =>
                          renderColumnHeader(column, section.backgroundColor)
                        )}
                      </Table.Tr>

                      {section.groups.map((group, index) => {
                        const rowBg = section.backgroundColor;
                        const typeBadge = getTypeBadgeStyles(group.kind);
                        const cellStyle = buildCellStyle(rowBg);
                        const expandCellStyle = buildExpandCellStyle(rowBg);
                        const secondaryText = getGroupSecondaryText(group);
                        const firstAlert = group.alerts[0];
                        const hospitalizationAlert =
                          firstAlert?.kind === "HOSPITALIZATION"
                            ? firstAlert
                            : undefined;

                        return (
                          <Table.Tr
                            key={group.groupKey}
                            style={{
                              backgroundColor: rowBg,
                              cursor: "default",
                            }}
                          >
                            {columnIds.has("series") && (
                              <Table.Td
                                style={{
                                  ...cellStyle,
                                  ...seriesColStyle,
                                  padding: "10px 4px",
                                }}
                              >
                                <Text
                                  fw={500}
                                  size='sm'
                                  c={gray[8]}
                                  ta='center'
                                  style={{ lineHeight: 1.35 }}
                                >
                                  {formatSeriesNumber(index + 1)}
                                </Text>
                              </Table.Td>
                            )}

                            {columnIds.has("icdCode") && (
                              <Table.Td
                                style={{
                                  ...cellStyle,
                                  paddingRight: `${ICD_COLUMN_GAP}px`,
                                }}
                              >
                                <Text
                                  fw={500}
                                  size='sm'
                                  c={gray[8]}
                                  style={{ lineHeight: 1.35 }}
                                >
                                  {group.icdCode || "--"}
                                </Text>
                                {renderHccBadge(group.v28)}
                              </Table.Td>
                            )}

                            {columnIds.has("icdDescription") && (
                              <Table.Td
                                style={{
                                  ...cellStyle,
                                  paddingLeft: `${ICD_COLUMN_GAP}px`,
                                }}
                              >
                                <Text
                                  fw={500}
                                  size='sm'
                                  c={gray[8]}
                                  style={{
                                    lineHeight: 1.35,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {group.icdCodeDescription || "--"}
                                </Text>
                                {secondaryText
                                  ? renderSecondaryText(secondaryText)
                                  : null}
                              </Table.Td>
                            )}

                            {columnIds.has("description") && (
                              <Table.Td
                                colSpan={descriptionColSpan}
                                style={cellStyle}
                              >
                                <Text
                                  fw={500}
                                  size='sm'
                                  c={gray[8]}
                                  style={{
                                    lineHeight: 1.35,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {group.description || "--"}
                                </Text>
                                {secondaryText
                                  ? renderSecondaryText(secondaryText)
                                  : null}
                              </Table.Td>
                            )}

                            {columnIds.has("type") && (
                              <Table.Td style={cellStyle}>
                                <Badge
                                  variant='light'
                                  radius='sm'
                                  size='lg'
                                  style={{
                                    color: typeBadge.color,
                                    backgroundColor: typeBadge.backgroundColor,
                                    border: `1px solid ${typeBadge.borderColor}`,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    whiteSpace: "normal",
                                    height: "auto",
                                    padding: "4px 10px",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {typeBadge.label}
                                </Badge>
                              </Table.Td>
                            )}

                            {columnIds.has("createdAt") && (
                              <Table.Td
                                style={{
                                  ...cellStyle,
                                  padding: `10px 16px 10px ${DATE_COLUMN_PADDING_LEFT}px`,
                                  boxSizing: "border-box",
                                }}
                              >
                                {hospitalizationAlert ? (
                                  <Text
                                    fw={500}
                                    size='sm'
                                    c={gray[8]}
                                    style={{
                                      lineHeight: 1.35,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {`${formatDateOnly(
                                      hospitalizationAlert.admissionDate
                                    )} / ${formatDateOnly(
                                      hospitalizationAlert.dischargeDate
                                    )}`}
                                  </Text>
                                ) : (
                                  <Text
                                    fw={500}
                                    size='sm'
                                    c={gray[8]}
                                    style={{
                                      lineHeight: 1.35,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {group.createdAt
                                      ? formatDateOnly(group.createdAt)
                                      : "--"}
                                  </Text>
                                )}
                              </Table.Td>
                            )}

                            {columnIds.has("action") && (
                              <Table.Td style={cellStyle}>
                                {renderActionStatus(
                                  group.alerts,
                                  group.status,
                                  rowBg,
                                  {
                                    isAcknowledging: acknowledgingKeys.has(
                                      group.groupKey
                                    ),
                                    onAcknowledge: (alertsToAcknowledge) => {
                                      const groupKey = group.groupKey;
                                      setAcknowledgingKeys((current) => {
                                        const next = new Set(current);
                                        next.add(groupKey);
                                        return next;
                                      });

                                      void Promise.resolve(
                                        onAcknowledge?.(alertsToAcknowledge)
                                      ).then((success) => {
                                        if (success === false) {
                                          setAcknowledgingKeys((current) => {
                                            const next = new Set(current);
                                            next.delete(groupKey);
                                            return next;
                                          });
                                        }
                                      });
                                    },
                                  }
                                )}
                              </Table.Td>
                            )}

                            {columnIds.has("expand") && (
                              <Table.Td style={expandCellStyle}>
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: "10px",
                                  }}
                                >
                                  {group.alertId && (
                                    <Tooltip
                                      label={group.alertId}
                                      withArrow
                                      position='left'
                                    >
                                      <Box
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          color: primary[5],
                                        }}
                                      >
                                        <IconInfoCircle size={16} />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Table.Td>
                            )}
                          </Table.Tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

            {!loading && sections.length === 0 && (
              <Table.Tr>
                <Table.Td
                  colSpan={maxColumnCount}
                  style={{ padding: "28px 16px" }}
                >
                  <Text ta='center' c={gray[8]} size='sm'>
                    No data available
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>
    </Box>
  );
};

export default VBCAlertsV2Table;
